import { Injectable, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { forkJoin, from, map, Observable, of, switchMap, throwError } from 'rxjs';
import { MessageResponse, Session, SessionResponse, TracerResponse } from '../models/tracer.model';
import { EvaluationResponse } from '../models/evaluation.model';
import { SkillsResponse, SkillResponse, TicketResponse, TicketSkill } from '../models/ticket.model';
import {
  AiSettingsResponse,
  AiModelsResponse,
  AiSyncResponse,
  AiTestResponse,
  AiSettingsBody,
  GithubStatusResponse,
  GithubReposResponse,
} from '../models/ai.model';
import {
  ManualGateBody,
  ManualGateResponse,
  ReplayEvent,
  ReplayHistoryResponse,
  ReplayPlanResponse,
  ReplayStatusResponse,
  ResultIO,
  StartReplayBody,
  StartReplayResponse,
} from '../models/replay.model';
import {
  ReportSummaryResponse,
  ReportBreakdownResponse,
  ReportDimension,
} from '../models/reports.model';
import {
  CreatePersonalTokenResponse,
  PersonalTokenResponse,
  PersonalTokensResponse,
} from '../models/personal-token.model';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { decodeMessageBody } from './decode-message-body';

@Injectable({
  providedIn: 'root',
})
export class TracerService {
  // Keycloak is injected so streamReplay() can pull the current access
  // token. EventSource cannot set custom headers, so we pass the token
  // via ?access_token= query param — see the same flow in the BE
  // AuthMiddlewareWithQueryFallback. HttpClient continues to use the
  // bearer interceptor for every other endpoint.
  private readonly keycloak = inject(Keycloak);

  constructor(private http: HttpClient) {}

  getTracerData(): Observable<TracerResponse> {
    const url = `${environment.tracerUrl}/index${window.location.search}`;

    return this.http.get<TracerResponse>(url, { withCredentials: true });
  }

  getStats(): Observable<{
    sessions: number;
    requests: number;
    sessions_replayed: number;
    requests_replayed: number;
  }> {
    const url = `${environment.tracerUrl}/stats`;
    return this.http.get<{
      sessions: number;
      requests: number;
      sessions_replayed: number;
      requests_replayed: number;
    }>(url);
  }

  getVersion(): Observable<{ name: string; version: string }> {
    const url = `${environment.tracerUrl}/version`;
    return this.http.get<{ name: string; version: string }>(url);
  }

  // Admin Reports. Both endpoints are admin-only on the backend and read
  // pre-aggregated rollups; the FE sends an explicit from/to window and
  // the backend chooses the rollup tier + bucket grain.
  getReportSummary(fromIso: string, toIso: string): Observable<ReportSummaryResponse> {
    const url = `${environment.tracerUrl}/admin/reports/summary?from=${encodeURIComponent(
      fromIso
    )}&to=${encodeURIComponent(toIso)}`;
    return this.http.get<ReportSummaryResponse>(url, { withCredentials: true });
  }

  getReportBreakdown(
    fromIso: string,
    toIso: string,
    dimension: ReportDimension,
    limit = 8
  ): Observable<ReportBreakdownResponse> {
    const url = `${environment.tracerUrl}/admin/reports/breakdown?from=${encodeURIComponent(
      fromIso
    )}&to=${encodeURIComponent(toIso)}&dimension=${dimension}&limit=${limit}`;
    return this.http.get<ReportBreakdownResponse>(url, { withCredentials: true });
  }

  // Session detail is paged: perPage is one of the sizes the backend
  // allows (25/50/100 — anything else is coerced server-side) and before
  // is the opaque next_cursor from the previous page, newest page first.
  getSessionData(query: string, perPage?: number, before?: string): Observable<SessionResponse> {
    const params = new URLSearchParams();
    if (perPage) params.set('per_page', String(perPage));
    if (before) params.set('before', before);
    const qs = params.toString();
    const url = `${environment.tracerUrl}/session/${query}${qs ? `?${qs}` : ''}`;
    return this.http.get<SessionResponse>(url, { withCredentials: true });
  }

  updateSessionData(query: string, session: Session): Observable<SessionResponse> {
    const url = `${environment.tracerUrl}/session/${query}`;
    return this.http.put<SessionResponse>(url, session, { withCredentials: true });
  }

  downloadZip(query: string, provider: string, type: string) {
    const url = `${environment.tracerUrl}/session-zip/${query}/${provider}/${type}`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      switchMap(response => {
        const contentType = response.headers.get('content-type');

        if (contentType?.includes('application/json')) {
          return from(response.body!.text()).pipe(
            switchMap(jsonText => {
              const jsonResponse = JSON.parse(jsonText);
              if ('presigned_urls' in jsonResponse) {
                const zip = new JSZip();
                const rootFolder = zip.folder(query)!;

                const downloadRequests = Object.entries(jsonResponse.presigned_urls).map(
                  ([key, presignedUrl]) => {
                    return this.http.get(presignedUrl as string, { responseType: 'text' }).pipe(
                      switchMap(async data => {
                        const [headers, bodyEncoded] = data.split('%DELIM%');
                        const body = await decodeMessageBody(bodyEncoded);
                        const fullContent = `${headers}\n\n${body}`;
                        rootFolder.file(key, fullContent);
                      })
                    );
                  }
                );

                return forkJoin(downloadRequests).pipe(
                  switchMap(() => from(zip.generateAsync({ type: 'blob' }))),
                  map(blob => saveAs(blob, `${query}.zip`))
                );
              }

              // Backend returned a JSON error (e.g. nothing matched the
              // provider/type selection). Surface it instead of silently
              // doing nothing — previously this saved a 0-byte .zip.
              if (jsonResponse?.error) {
                return throwError(() => new Error(jsonResponse.error));
              }

              return of(null);
            })
          );
        } else {
          // Direct download case
          const blob = response.body!;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${query}.zip`);
          document.body.appendChild(link);
          link.click();
          return of(null);
        }
      })
    );
  }

  getMessage(id: number): Observable<MessageResponse> {
    const url = `${environment.tracerUrl}/message/${id}`;
    return this.http.get<MessageResponse>(url, { withCredentials: true });
  }

  // AI evaluation of a session. `force=true` bypasses the server-side
  // cache and triggers a fresh Vertex call. Endpoint is admin-only.
  evaluateSession(sessionValue: string, force = false): Observable<EvaluationResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/evaluate${force ? '?force=true' : ''}`;
    return this.http.post<EvaluationResponse>(url, {}, { withCredentials: true });
  }

  // --- AI Ticket (admin-only) --------------------------------------

  /** Generate (or return cached) an AI ticket for a session under a skill. */
  generateTicket(
    sessionValue: string,
    skillId: number,
    supportGuidance = '',
    force = false
  ): Observable<TicketResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/ticket`;
    return this.http.post<TicketResponse>(
      url,
      { skill_id: skillId, support_guidance: supportGuidance, force },
      { withCredentials: true }
    );
  }

  /** List skills. `enabledOnly` is for the generation dialog picker. */
  listSkills(enabledOnly = false): Observable<SkillsResponse> {
    const url = `${environment.tracerUrl}/admin/skills${enabledOnly ? '?enabled=true' : ''}`;
    return this.http.get<SkillsResponse>(url, { withCredentials: true });
  }

  getSkill(id: number): Observable<SkillResponse> {
    const url = `${environment.tracerUrl}/admin/skills/${id}`;
    return this.http.get<SkillResponse>(url, { withCredentials: true });
  }

  createSkill(skill: Partial<TicketSkill>): Observable<SkillResponse> {
    const url = `${environment.tracerUrl}/admin/skills`;
    return this.http.post<SkillResponse>(url, skill, { withCredentials: true });
  }

  updateSkill(id: number, skill: Partial<TicketSkill>): Observable<SkillResponse> {
    const url = `${environment.tracerUrl}/admin/skills/${id}`;
    return this.http.put<SkillResponse>(url, skill, { withCredentials: true });
  }

  /** Default: soft-disable. Pass hard=true to drop the row outright. */
  deleteSkill(id: number, hard = false): Observable<void> {
    const url = `${environment.tracerUrl}/admin/skills/${id}${hard ? '?hard=true' : ''}`;
    return this.http.delete<void>(url, { withCredentials: true });
  }

  getMessageFromPresignedUrl(url: string): Observable<any> {
    const presignedUrl = decodeURIComponent(url);
    return this.http.get(presignedUrl, { withCredentials: false, responseType: 'text' });
  }

  // --- AI Settings (admin-only) ------------------------------------

  getAiSettings(): Observable<AiSettingsResponse> {
    const url = `${environment.tracerUrl}/admin/ai/settings`;
    return this.http.get<AiSettingsResponse>(url, { withCredentials: true });
  }

  putAiSettings(body: AiSettingsBody): Observable<AiSettingsResponse> {
    const url = `${environment.tracerUrl}/admin/ai/settings`;
    return this.http.put<AiSettingsResponse>(url, body, { withCredentials: true });
  }

  listAiModels(region?: string): Observable<AiModelsResponse> {
    const q = region ? `?region=${encodeURIComponent(region)}` : '';
    const url = `${environment.tracerUrl}/admin/ai/models${q}`;
    return this.http.get<AiModelsResponse>(url, { withCredentials: true });
  }

  syncAiModels(): Observable<AiSyncResponse> {
    const url = `${environment.tracerUrl}/admin/ai/models/sync`;
    return this.http.post<AiSyncResponse>(url, {}, { withCredentials: true });
  }

  testAiModel(body: {
    project_id?: string;
    region: string;
    model_id: string;
    model_version?: string;
  }): Observable<AiTestResponse> {
    const url = `${environment.tracerUrl}/admin/ai/settings/test`;
    return this.http.post<AiTestResponse>(url, body, { withCredentials: true });
  }

  // --- GitHub issue integration (admin-only) -----------------------

  getGithubStatus(): Observable<GithubStatusResponse> {
    const url = `${environment.tracerUrl}/admin/ai/github`;
    return this.http.get<GithubStatusResponse>(url, { withCredentials: true });
  }

  /** Connect (owner/repo) or disconnect ("") the instance-wide repo. */
  setGithubRepo(repo: string): Observable<GithubStatusResponse> {
    const url = `${environment.tracerUrl}/admin/ai/github`;
    return this.http.put<GithubStatusResponse>(url, { repo }, { withCredentials: true });
  }

  /** Validate + store a pasted GitHub token ("" clears it). */
  setGithubToken(token: string): Observable<GithubStatusResponse> {
    const url = `${environment.tracerUrl}/admin/ai/github/token`;
    return this.http.put<GithubStatusResponse>(url, { token }, { withCredentials: true });
  }

  /** Repositories the connected token can push to (for the picker). */
  getGithubRepos(): Observable<GithubReposResponse> {
    const url = `${environment.tracerUrl}/admin/ai/github/repos`;
    return this.http.get<GithubReposResponse>(url, { withCredentials: true });
  }

  /** Clear the imported OAuth token (repo selection is kept). */
  disconnectGithub(): Observable<GithubStatusResponse> {
    const url = `${environment.tracerUrl}/admin/ai/github/disconnect`;
    return this.http.post<GithubStatusResponse>(url, {}, { withCredentials: true });
  }

  /** Create (or return the existing) GitHub issue for a ticket. */
  createGithubIssue(sessionValue: string, ticketId: number): Observable<TicketResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/ticket/github-issue`;
    return this.http.post<TicketResponse>(url, { ticket_id: ticketId }, { withCredentials: true });
  }

  // --- Personal API tokens (admin-only) ----------------------------
  //
  // These four endpoints are refused for requests that authenticated
  // WITH a personal token — only an interactive Keycloak session can
  // manage tokens, so a leaked token can neither mint a successor nor
  // delete itself. Nothing to do on this side beyond surfacing the
  // 403 message if it ever happens.

  listPersonalTokens(): Observable<PersonalTokensResponse> {
    const url = `${environment.tracerUrl}/admin/tokens`;
    return this.http.get<PersonalTokensResponse>(url, { withCredentials: true });
  }

  /** Mint a token. The `token` field of the response is the plaintext
   *  secret and is the ONLY time it is ever available — show it, then
   *  drop it. */
  createPersonalToken(name: string): Observable<CreatePersonalTokenResponse> {
    const url = `${environment.tracerUrl}/admin/tokens`;
    return this.http.post<CreatePersonalTokenResponse>(url, { name }, { withCredentials: true });
  }

  /** Soft kill: stops working immediately, row + usage history kept. */
  revokePersonalToken(id: number): Observable<PersonalTokenResponse> {
    const url = `${environment.tracerUrl}/admin/tokens/${id}/revoke`;
    return this.http.post<PersonalTokenResponse>(url, {}, { withCredentials: true });
  }

  /** Hard delete: drops the row entirely. */
  deletePersonalToken(id: number): Observable<void> {
    const url = `${environment.tracerUrl}/admin/tokens/${id}`;
    return this.http.delete<void>(url, { withCredentials: true });
  }

  // --- Repeat Session (admin-only) ---------------------------------

  /** Plan endpoint: list of requests in the session with replayability hints. */
  getReplayPlan(sessionValue: string): Observable<ReplayPlanResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/replay/plan`;
    return this.http.get<ReplayPlanResponse>(url, { withCredentials: true });
  }

  /** Kick off a replay run. Returns immediately with the new replay_id. */
  startReplay(sessionValue: string, body: StartReplayBody): Observable<StartReplayResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/replay`;
    return this.http.post<StartReplayResponse>(url, body, { withCredentials: true });
  }

  /** Snapshot of replay status + per-request rows. Used as polling fallback. */
  getReplay(sessionValue: string, replayId: number): Observable<ReplayStatusResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/replay/${replayId}`;
    return this.http.get<ReplayStatusResponse>(url, { withCredentials: true });
  }

  /** Recent replays for the session (history strip). */
  listReplays(sessionValue: string): Observable<ReplayHistoryResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/replays`;
    return this.http.get<ReplayHistoryResponse>(url, { withCredentials: true });
  }

  /** Verbose-view payload for one row. Lazy-loaded when the user expands. */
  /**
   * Manual-mode gate release. Advances or skips the request the executor
   * is currently parked on. Returns 200 on success; backend uses 4xx for
   * mode mismatch (400), no replay (404), no waiting gate (409),
   * terminal run (410). Caller may surface those distinctly.
   */
  advanceManualReplay(
    sessionValue: string,
    replayId: number,
    body: ManualGateBody
  ): Observable<ManualGateResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/replay/${replayId}/advance`;
    return this.http.post<ManualGateResponse>(url, body, { withCredentials: true });
  }

  skipManualReplay(
    sessionValue: string,
    replayId: number,
    body: ManualGateBody
  ): Observable<ManualGateResponse> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/replay/${replayId}/skip`;
    return this.http.post<ManualGateResponse>(url, body, { withCredentials: true });
  }

  getReplayResultIO(
    sessionValue: string,
    replayId: number,
    resultId: number
  ): Observable<ResultIO> {
    const url = `${environment.tracerUrl}/session/${sessionValue}/replay/${replayId}/result/${resultId}/io`;
    return this.http.get<ResultIO>(url, { withCredentials: true });
  }

  /**
   * Subscribe to a replay's live progress via SSE. Emits one event per
   * server frame; completes when the EventSource closes (terminal status
   * or network error).
   *
   * Caller is responsible for switching to `getReplay` polling if this
   * Observable errors — the dialog does that automatically.
   */
  streamReplay(sessionValue: string, replayId: number): Observable<ReplayEvent> {
    // The browser EventSource API can't set request headers, so we
    // smuggle the bearer token through `?access_token=` and the realm
    // through `?ag_realm=`. The BE's AuthMiddlewareWithQueryFallback
    // (only wired on this one route) reconstitutes the canonical
    // Authorization header from these. Keycloak access tokens are
    // short-lived (~5 min), so the URL leak surface is bounded.
    const token = this.keycloak.token || '';
    const realm =
      this.keycloak.realm ||
      // Fallback for the dev-mode auth-guard path that stores the
      // resolved realm in localStorage.
      localStorage.getItem('agRealm') ||
      '';
    const params = new URLSearchParams();
    if (token) params.set('access_token', token);
    if (realm) params.set('ag_realm', realm);
    const qs = params.toString();
    const url =
      `${environment.tracerUrl}/session/${sessionValue}/replay/${replayId}/stream` +
      (qs ? `?${qs}` : '');
    return new Observable<ReplayEvent>(subscriber => {
      const source = new EventSource(url, { withCredentials: true });
      const handle = (eventType: ReplayEvent['type']) => (e: MessageEvent) => {
        try {
          const payload = JSON.parse(e.data);
          subscriber.next({ type: eventType, ...payload });
        } catch (err) {
          // malformed frame — surface as a stream error so the
          // caller flips to polling
          subscriber.error(err);
        }
      };
      source.addEventListener('snapshot', handle('snapshot') as EventListener);
      source.addEventListener('replay', handle('replay') as EventListener);
      source.addEventListener('result', handle('result') as EventListener);
      // step events fire at every executor phase boundary
      // (loading_payload / shifting_dates / firing_http /
      // awaiting_response / received_response / persisting) when the
      // run's verbosity is Detailed or Verbose. The BE publishes them
      // via the broker but the FE used to silently miss them because
      // no listener was registered — so the panel's italic "what's
      // happening" line under each row never updated and the replay
      // log dropped req.step rows entirely. Without these, long-
      // upstream replays look frozen between markResult(RUNNING) and
      // markResult(OK).
      source.addEventListener('step', handle('step') as EventListener);
      source.addEventListener(
        'awaiting_confirmation',
        handle('awaiting_confirmation') as EventListener
      );
      source.addEventListener('session_linked', handle('session_linked') as EventListener);
      source.addEventListener('replay_timeout', handle('replay_timeout') as EventListener);
      source.addEventListener('error', () => {
        // EventSource fires `error` on disconnect AND on network
        // failure. Treat it as terminal — the run might be done or the
        // connection broke; either way the UI should fall back to
        // polling.
        subscriber.error(new Error('SSE connection error'));
      });
      return () => source.close();
    });
  }
}
